import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { 
  ArrowRight, Mic, Brain, Zap, CheckCircle, MessageSquare, 
  AlertTriangle, Database, Phone, Server, Activity, Heart,
  TrendingUp, Target, Users, Workflow, Building, DollarSign,
  Shield, Gauge, Code, Sparkles
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import WorkflowVisualization from '../components/voice/WorkflowVisualization';
import LiveAgentStatus from '../components/voice/LiveAgentStatus';
import AgentStateFlow from '../components/voice/AgentStateFlow';
import {
  agentCapabilities,
  evolutionStages,
  agentLifecycle,
  benefitVerificationSteps,
  voiceAgentStack,
  howWeWork,
  vedaraBeliefs,
  longHorizonState
} from '../data/voiceAgentData';

const Home = () => {
  const [activeLifecycleStep, setActiveLifecycleStep] = useState(0);

  const capabilityIcons = {
    mic: Mic,
    brain: Brain,
    zap: Zap,
    check: CheckCircle,
    message: MessageSquare,
    alert: AlertTriangle,
    database: Database
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-slate-900 to-gray-900">
      <Navbar />

      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-purple-900/20" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-mono tracking-wider">
              VOICE AGENTS, IN PRODUCTION.
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
            AI that doesn't just talk.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400">
              It gets work done.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-6 leading-relaxed">
            Vedara builds autonomous Voice Agents that can call, listen, reason, act, and complete complex enterprise workflows.
          </p>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            From navigating IVRs and speaking with humans to calling APIs and updating enterprise systems, we take Voice AI from prototype to production.
          </p>

          <div className="mb-12 p-6 rounded-xl bg-gray-900/50 border border-gray-800 max-w-2xl mx-auto">
            <p className="text-lg md:text-xl font-semibold text-white mb-2">
              No services fees. No billable hours.
            </p>
            <p className="text-md md:text-lg text-gray-400">
              We succeed when your agents succeed.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-blue-500/50">
              Build a Voice Agent <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button size="lg" variant="outline" className="border-gray-600 hover:border-blue-500 text-white px-8 py-6 text-lg rounded-xl">
              See What We Build <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>

          <div className="flex justify-center mb-16">
            <LiveAgentStatus />
          </div>

          <div className="mt-16">
            <WorkflowVisualization />
          </div>
        </div>
      </section>

      {/* ========== THE BIG SHIFT ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-8 text-center">
            <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
              The next interface for AI is voice.
            </h2>
            
            <div className="prose prose-lg prose-invert max-w-none space-y-6 text-gray-300 text-lg md:text-xl leading-relaxed">
              <p>
                For decades, software has required people to learn how to use it.
              </p>
              <div className="flex flex-col items-center space-y-2 text-gray-500 font-mono text-base py-4">
                <span>Click.</span>
                <span>Search.</span>
                <span>Type.</span>
                <span>Navigate.</span>
                <span>Fill out forms.</span>
              </div>
              <p>
                But people already know how to communicate.
                <br />
                <span className="text-white font-semibold text-2xl">They know how to talk.</span>
              </p>
              <p className="pt-8">
                The next generation of AI agents won't simply live inside applications.
              </p>
              <p className="text-white font-semibold text-xl">
                They will:
              </p>
              <ul className="list-none space-y-3 text-blue-400 text-lg">
                <li className="flex items-center justify-center"><ArrowRight className="mr-2" size={20} /> Call people.</li>
                <li className="flex items-center justify-center"><ArrowRight className="mr-2" size={20} /> Talk to systems.</li>
                <li className="flex items-center justify-center"><ArrowRight className="mr-2" size={20} /> Navigate workflows.</li>
                <li className="flex items-center justify-center"><ArrowRight className="mr-2" size={20} /> Take action.</li>
              </ul>
            </div>

            <div className="pt-12">
              <WorkflowVisualization />
            </div>

            <p className="text-2xl md:text-3xl font-bold text-white pt-12">
              Vedara is building the production layer for this transition.
            </p>
          </div>
        </div>
      </section>

      {/* ========== TALKING IS EASY ========== */}
      <section id="what-we-build" className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Talking is easy.
              <br />
              <span className="text-blue-400">Getting things done is hard.</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A production agent needs to do more than chat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {agentCapabilities.map((capability, index) => {
              const Icon = capabilityIcons[capability.icon];
              return (
                <Card key={index} className="bg-gray-900/50 border border-gray-800 p-6 hover:border-blue-500/50 transition-all hover:scale-105">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Icon className="text-blue-400" size={28} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{capability.title}</h3>
                      <p className="text-sm text-gray-400">{capability.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })  }
          </div>
        </div>
      </section>

      {/* ========== THE EVOLUTION ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {evolutionStages.map((stage, index) => (
              <div key={index} className="text-center space-y-4">
                <div className={`text-sm font-mono mb-2 ${index === 3 ? 'text-blue-400' : 'text-gray-500'}`}>
                  {stage.year}
                </div>
                <div className={`text-2xl font-bold mb-4 ${index === 3 ? 'text-blue-400' : 'text-white'}`}>
                  {stage.stage}
                </div>
                <div className={`text-lg italic ${index === 3 ? 'text-white' : 'text-gray-400'}`}>
                  {stage.description}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <p className="text-3xl font-bold text-blue-400">
              "I'll take care of it until it's done."
            </p>
          </div>
        </div>
      </section>

      {/* ========== DON'T AUTOMATE THE CALL ========== */}
      <section className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-white text-center mb-12">
            Don't automate the call.
            <br />
            <span className="text-blue-400">Automate the outcome.</span>
          </h2>

          <div className="space-y-8 text-center text-lg text-gray-300 mb-16">
            <p>Most Voice AI products optimize the conversation.</p>
            <p className="text-white text-2xl font-semibold">Vedara optimizes the workflow.</p>
            <p>A call may be only one step in a much larger process.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="p-8 bg-gray-900/50 border border-gray-800 rounded-xl">
              <h3 className="text-xl font-bold text-gray-400 mb-6">Traditional</h3>
              <div className="flex items-center justify-between text-gray-300">
                <span>Call</span>
                <ArrowRight size={20} />
                <span>Talk</span>
                <ArrowRight size={20} />
                <span>Answer</span>
                <ArrowRight size={20} />
                <span>End</span>
              </div>
            </div>
            <div className="p-8 bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/50 rounded-xl">
              <h3 className="text-xl font-bold text-blue-400 mb-6">Vedara</h3>
              <div className="flex flex-wrap gap-2 text-sm text-blue-300">
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Goal</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Plan</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Call</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Reason</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Act</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Wait</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Follow Up</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Verify</span>
                <ArrowRight size={16} className="mt-1" />
                <span className="px-3 py-1 bg-blue-500/20 rounded-full">Complete</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-2xl p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">Long-Horizon Voice Agents</h3>
            <p className="text-xl text-gray-300 mb-6">
              Some enterprise workflows don't take five minutes.
            </p>
            <p className="text-lg text-gray-400">
              They take <span className="text-white font-semibold">hours</span>, <span className="text-white font-semibold">days</span>, or <span className="text-white font-semibold">weeks</span>.
            </p>
            <p className="text-xl text-blue-400 font-bold mt-8">
              The agent doesn't stop when the call ends.
            </p>
          </div>
        </div>
      </section>

      {/* ========== AGENT LIFECYCLE ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            From first word to final outcome.
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {agentLifecycle.map((item, index) => (
              <div 
                key={index}
                className="flex flex-col items-center text-center space-y-3 p-4 rounded-xl bg-gray-900/50 border border-gray-800 hover:border-blue-500/50 hover:scale-105 transition-all cursor-pointer"
                onMouseEnter={() => setActiveLifecycleStep(index)}
              >
                <div className={`text-2xl font-bold ${activeLifecycleStep === index ? 'text-blue-400' : 'text-gray-600'}`}>
                  {item.step}
                </div>
                <div className={`text-sm font-bold ${activeLifecycleStep === index ? 'text-white' : 'text-gray-400'}`}>
                  {item.title}
                </div>
                <div className="text-xs text-gray-500">
                  {item.description}
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xl text-gray-400 mt-16">
            The agent is continuously moving toward an outcome.
          </p>
        </div>
      </section>

      {/* ========== SIGNATURE USE CASE ========== */}
      <section className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Meet the Benefit Verification Agent
            </h2>
            <p className="text-xl text-gray-400">
              A phone call that becomes an autonomous workflow.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-gray-900/50 border border-gray-800 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-white mb-6">Today:</h3>
            <p className="text-gray-400 mb-8">
              Staff → Call payer → Navigate IVR → Wait → Talk → Take notes → Verify → Enter EHR
            </p>

            <h3 className="text-2xl font-bold text-blue-400 mb-6">With Vedara:</h3>
            <p className="text-xl text-white mb-8">The Agent does it.</p>

            <AgentStateFlow steps={benefitVerificationSteps} autoPlay={true} speed={2500} />
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-blue-400">
              The agent doesn't just complete the call.
              <br />
              It completes the workflow.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VOICE AGENT STACK ========== */}
      <section className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              A production Voice Agent is more than a model.
            </h2>
            <p className="text-xl text-gray-400">
              Vedara brings together the entire stack.
            </p>
          </div>

          <div className="space-y-4">
            {voiceAgentStack.map((layer, index) => (
              <div 
                key={index}
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-blue-500/50 transition-all"
              >
                <div className="flex items-start space-x-4">
                  <div className="text-4xl font-bold text-blue-500/30">{layer.layer}</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-4">{layer.title}</h3>
                    <div className="flex flex-wrap gap-2">
                      {layer.capabilities.map((cap, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-800/50 text-gray-300 text-sm rounded-full border border-gray-700">
                          {cap}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-2xl font-bold text-white">
              We don't just build the brain.
              <br />
              <span className="text-blue-400">We build everything around it.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========== LONG-HORIZON ARCHITECTURE ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-12">
            Voice Agents with memory, state and persistence.
          </h2>

          <p className="text-xl text-gray-300 text-center mb-16">
            Long-horizon agents need to understand more than conversational context.
          </p>

          <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-blue-400 mb-8 text-center">Persistent Agent State</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800">
                <div className="text-sm font-mono text-gray-500 mb-2">OBJECTIVE</div>
                <div className="text-white font-semibold">{longHorizonState.objective}</div>
              </div>
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800">
                <div className="text-sm font-mono text-gray-500 mb-2">CURRENT STATE</div>
                <div className="text-yellow-400 font-semibold">{longHorizonState.currentState}</div>
              </div>
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800">
                <div className="text-sm font-mono text-gray-500 mb-2">COMPLETED</div>
                <div className="text-green-400 font-semibold">{longHorizonState.completed[0]}</div>
              </div>
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800">
                <div className="text-sm font-mono text-gray-500 mb-2">OUTSTANDING</div>
                <div className="text-red-400 font-semibold">{longHorizonState.outstanding[0]}</div>
              </div>
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800">
                <div className="text-sm font-mono text-gray-500 mb-2">NEXT ACTION</div>
                <div className="text-blue-400 font-semibold">{longHorizonState.nextAction}</div>
              </div>
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800">
                <div className="text-sm font-mono text-gray-500 mb-2">SUCCESS CRITERIA</div>
                <div className="text-white font-semibold">{longHorizonState.successCriteria}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PRODUCTION IS THE PRODUCT ========== */}
      <section className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-white text-center mb-12">
            The demo is easy.
            <br />
            <span className="text-blue-400">Production is the product.</span>
          </h2>

          <div className="space-y-8 text-center text-lg text-gray-300 mb-16">
            <p>A demo can survive perfect conditions.</p>
            <p className="text-white text-xl font-semibold">Production cannot.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-xl font-bold text-gray-400 mb-6">A demo survives:</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start"><span className="mr-2">•</span>Perfect audio</li>
                <li className="flex items-start"><span className="mr-2">•</span>Perfect inputs</li>
                <li className="flex items-start"><span className="mr-2">•</span>Perfect APIs</li>
                <li className="flex items-start"><span className="mr-2">•</span>Perfect users</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-400 mb-6">Vedara engineers for:</h3>
              <ul className="space-y-3 text-blue-300">
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={16} />Accents & interruptions</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={16} />Background noise & silence</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={16} />IVRs & transfers</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={16} />API failures & latency</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={16} />Legacy systems</li>
                <li className="flex items-start"><CheckCircle className="mr-2 flex-shrink-0 mt-1" size={16} />Edge cases & escalation</li>
              </ul>
            </div>
          </div>

          <p className="text-2xl font-bold text-center text-white">
            We engineer for the first mile and the last mile.
          </p>
        </div>
      </section>

      {/* ========== COMMERCIAL MODEL ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              We succeed when our customers succeed.
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Rather than making the engagement about hours, effort, or project scope, we focus on helping our customers get their AI agents into production and create meaningful value.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="p-8 bg-gray-900/50 border border-gray-800 rounded-xl">
              <h3 className="text-xl font-bold text-gray-400 mb-6">Traditional services:</h3>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <Users size={24} />
                  <ArrowRight size={20} />
                  <span>People</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Activity size={24} />
                  <ArrowRight size={20} />
                  <span>Hours</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Workflow size={24} />
                  <ArrowRight size={20} />
                  <span>Projects</span>
                </div>
                <div className="flex items-center space-x-3">
                  <DollarSign size={24} />
                  <ArrowRight size={20} />
                  <span>Fees</span>
                </div>
              </div>
            </div>

            <div className="p-8 bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/50 rounded-xl">
              <h3 className="text-xl font-bold text-blue-400 mb-6">Vedara:</h3>
              <div className="space-y-4 text-blue-300">
                <div className="flex items-center space-x-3">
                  <Brain size={24} />
                  <ArrowRight size={20} />
                  <span>Agents</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Sparkles size={24} />
                  <ArrowRight size={20} />
                  <span>Production</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Target size={24} />
                  <ArrowRight size={20} />
                  <span>Outcomes</span>
                </div>
                <div className="flex items-center space-x-3">
                  <TrendingUp size={24} />
                  <ArrowRight size={20} />
                  <span>Shared Success</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-16 p-8 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <p className="text-xl text-white">
              If the customer doesn't succeed, Vedara doesn't succeed.
            </p>
          </div>
        </div>
      </section>

      {/* ========== HOW WE WORK ========== */}
      <section id="how-we-work" className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            Workflow → Agent → Production → Outcomes
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howWeWork.map((item, index) => (
              <div key={index} className="bg-gray-900/50 border border-gray-800 p-6 rounded-xl hover:border-blue-500/50 transition-all">
                <div className="text-4xl font-bold text-blue-500/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY VEDARA ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-12">
            Because production Voice AI is an engineering problem.
          </h2>

          <div className="text-center space-y-4 text-xl text-gray-400 mb-16">
            <p>Not just an LLM problem.</p>
            <p>Not just a telephony problem.</p>
            <p>Not just a prompt problem.</p>
            <p>Not just an integration problem.</p>
            <p className="text-white font-bold text-2xl pt-4">It's all of them.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {['Agent Engineering', 'Voice Engineering', 'Enterprise Engineering', 'Production Engineering', 'Evaluation Engineering', 'Deployment Engineering'].map((eng, i) => (
              <div key={i} className="p-6 bg-gray-900/50 border border-gray-800 rounded-xl text-center">
                <Code className="mx-auto mb-3 text-blue-400" size={32} />
                <div className="text-white font-semibold">{eng}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== VEDARA PHILOSOPHY ========== */}
      <section className="py-32 px-4 bg-black/40 relative border-t border-gray-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16">We believe:</h2>
          
          <div className="space-y-6">
            {vedaraBeliefs.map((belief, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-gray-900/50 border border-gray-800 rounded-xl hover:border-blue-500/50 transition-all">
                <CheckCircle className="text-blue-400 flex-shrink-0 mt-1" size={24} />
                <p className="text-lg text-gray-300">{belief}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-32 px-4 relative border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
            What should your agent be doing?
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Tell us the workflow.
            <br />
            We'll show you what it could become.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-8 text-xl rounded-xl shadow-lg shadow-blue-500/50">
                Build a Voice Agent <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-gray-600 hover:border-blue-500 text-white px-12 py-8 text-xl rounded-xl">
              Explore Use Cases <ArrowRight className="ml-2" size={24} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
